<template>
  <div v-if="useCompetitors" :class="[classes.container, layoutClass]">
    <div :class="[classes.badges, layoutClass]">
      <Competitors
        :eventId="eventId"
        :displayName="!stackedBadges"
        :class="[classes.competitors, 'competitors', layoutClass]"
        :vsHorizontal="vsHorizontalLine"
        layout="row"
      />
    </div>
    <div v-if="stackedBadges" :class="classes.teams">
      <DynamicText
        tag="div"
        styleGroup="Event Name"
        :get="fixtureNameStyle.get"
        :set="fixtureNameStyle.set"
        :class="classes.eventName"
      >
        {{ competitors?.[0].name.short }}
      </DynamicText>
      <div :class="classes.divider" />
      <DynamicText
        tag="div"
        styleGroup="Event Name"
        :get="fixtureNameStyle.get"
        :set="fixtureNameStyle.set"
        :class="classes.eventName"
      >
        {{ competitors?.[1].name.short }}
      </DynamicText>
    </div>
  </div>

  <LinkWrapper
    v-else
    :href="eventLink"
    target="_blank"
    @click="() => handleClick()"
  >
    <div :class="classes.eventWrapper">
      <FontFillText
        :text="eventName"
        styleGroup="Event Name"
        :getFont="fixtureNameStyle.get"
        :setFont="fixtureNameStyle.set"
        :minSize="eventTextSizes.min"
        :maxSize="eventTextSizes.max"
        :lineHeight="1.2"
        alignment="center"
        justifyContent="center"
      />
    </div>
  </LinkWrapper>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, ComputedRef } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import Competitors from "./CompetitorsBadges.vue";
import DynamicText from "./DynamicText.vue";
import LinkWrapper from "./LinkWrapper.vue";
import FontFillText from "./FontFillText.vue";
import {
  composeEvent,
  composeEventCompetitors
} from "@fresh8/ad-data-composition/src/betData";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { Brand } from "@fresh8/types/src";
import {
  injectTemplateSize,
  SKYSCRAPER
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectCardNumber, useAnalytics } from "../hooks/useAnalytics";
import { injectCarouselContext } from "../lib/provideInjectors";
import { injectTeamColors } from "../lib/teamBranding";
import { SportCompetitor } from "@fresh8/types/src/betData";

const useStyles = createUseStyles({
  container: {
    height: props => (props.stackedBadges ? "123px" : "112px"),
    display: props => (props.stackedBadges ? "grid" : ""),
    gridTemplateRows: props => (props.stackedBadges ? "2fr 1fr" : ""),
    paddingTop: "11px",
    paddingBottom: props => (props.badgesShowing ? "" : "11px"),
    [`&.${SKYSCRAPER.name}`]: {
      paddingBottom: "11px",
      height: "114px"
    }
  },
  badges: {
    height: props => (props.badgesShowing ? "80px" : "inherit"),
    position: "relative",
    width: "100%",
    display: "grid",
    gridTemplateColumns: props => (props.badgesShowing ? "1fr 1fr" : ""),
    gridTemplateRows: props => (props.badgesShowing ? "" : "1fr 1fr 1fr"),

    "& .helmet": {
      position: "relative",
      top: "-1px"
    },

    "& .team-badge": {
      borderWidth: "3px",
      maxWidth: "80px",
      width: props => (props.isCarousel ? "70px !important" : "80px !important")
    },

    "& .team-jersey": {
      borderWidth: "3px",
      maxWidth: "80px",
      width: props => (props.isCarousel ? "70px !important" : "80px !important")
    },

    "& .team-name": {
      fontSize: props => (props.badgesShowing ? "12px" : "16px")
    },

    "& .left": {
      paddingRight: props =>
        props.isCarousel ? "0px !important" : "8px !important",
      marginRight: "15px",
      "& .team-badge": {
        marginLeft: "5px"
      },
      "& .team-jersey": {
        marginLeft: "5px"
      },
      "& .team-name": {
        margin: props => (props.badgesShowing ? "0 12% 0 5%" : "0 14% 0 auto"),
        paddingLeft: props => {
          if (!props.badgesShowing) {
            return props.isCarousel ? "16px" : "32px";
          } else return "";
        }
      }
    },

    "& .right": {
      paddingLeft: props =>
        props.isCarousel ? "0px !important" : "8px !important",
      marginLeft: "15px",
      "& .team-badge": {
        marginRight: "5px"
      },
      "& .team-jersey": {
        marginRight: "5px"
      },
      "& .team-name": {
        margin: props => (props.badgesShowing ? "0 5% 0 12%" : "0 auto 0 14%"),
        paddingRight: props => {
          if (!props.badgesShowing) {
            return props.isCarousel ? "14px" : "28px";
          } else return "";
        }
      }
    },

    [`&.${SKYSCRAPER.name}`]: {
      height: "inherit",
      gridTemplateColumns: "1fr",
      gridTemplateRows: "1fr 1fr 1fr",
      "& .banner": {
        height: "28px"
      },
      "& .left": {
        "& .team-name": {
          paddingLeft: props => {
            if (!props.badgesShowing) {
              return props.isCarousel ? "16px" : "23px";
            } else return "";
          }
        }
      },
      "& .right": {
        "& .team-name": {
          paddingRight: props => {
            if (!props.badgesShowing) {
              return props.isCarousel ? "14px" : "23px";
            } else return "";
          }
        }
      },
      "& .team-name": {
        fontSize: "10px",
        margin: "0 auto"
      },
      "& .team-badge": {
        borderWidth: "1px",
        maxWidth: "40px",
        width: props =>
          props.isCarousel ? "40px !important" : "40px !important"
      },
      "& .team-jersey": {
        borderWidth: "1px",
        minWidth: "40px",
        width: props =>
          props.isCarousel ? "40px !important" : "40px !important"
      }
    }
  },
  teams: {
    display: "flex",
    justifyContent: "space-between"
  },
  competitors: {
    display: "flex",
    "& .banner": {
      width: "100%",
      minWidth: "100px",
      maxWidth: props => (props.isCarousel ? "230px" : "260px"),
      height: "34px"
    },

    "&:last-of-type": {
      justifyContent: "flex-end"
    },

    "&.vs": {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      zIndex: 1,
      fontSize: "12px",
      width: "inherit",
      position: props => (props.stackedBadges ? "absolute" : "inherit"),
      height: props => (props.stackedBadges ? "90%" : ""),
      top: props => (props.stackedBadges ? "50%" : ""),
      left: props => (props.stackedBadges ? "50%" : ""),
      transform: props => (props.stackedBadges ? "translate(-50%, -50%)" : "")
    },

    [`&.${SKYSCRAPER.name}`]: {
      "&.vs-icon": {
        minWidth: "104px !important"
      }
    }
  },
  eventWrapper: {
    width: "inherit",
    minHeight: "55px",
    height: "55px",
    maxHeight: "134px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "8px"
  },
  eventName: {
    width: props => (props.isCarousel ? "106px" : "118px"),
    display: "flex",
    fontSize: "12px",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    paddingBottom: 0
  },
  linkWrapper: {
    gridColumn: "1 / -1"
  },
  divider: {
    height: "70px",
    width: "1px",
    position: "absolute",
    backgroundColor: props =>
      props.marketBorderColor ? `${props.marketBorderColor}` : "#dcddf",
    left: "50%",
    top: "85px"
  }
});

export default defineComponent({
  name: "EventName",
  components: {
    Competitors,
    DynamicText,
    LinkWrapper,
    FontFillText
  },
  props: {
    eventId: { type: String, required: true },
    forceEventName: { type: Boolean, default: false },
    layout: {
      type: String as PropType<"column" | "row">,
      default: "row"
    }
  },
  setup(props) {
    const carouselContext = injectCarouselContext();
    const event = composeEvent(props.eventId);
    const brandContext = injectBrand();
    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      { ...props, cardNumber },
      "Event Name"
    );
    const competitors = composeEventCompetitors(props.eventId) as ComputedRef<
      SportCompetitor[]
    >;
    const teamColorsContext = injectTeamColors();
    const layoutClass = injectTemplateSize();

    const badgesEnabled = computed(
      () => teamColorsContext.teamIconsAvailable.value
    );

    const stackedBadges = computed(
      () =>
        badgesEnabled.value && layoutClass?.size.value.name !== SKYSCRAPER.name
    );

    const useCompetitors = computed(() => {
      return (
        !props.forceEventName &&
        (event.value?.competitors.home.name.full?.length || 0) > 0 &&
        (event.value?.competitors.away.name.full?.length || 0) > 0
      );
    });

    const reactiveStyles = computed(() => ({
      badgesShowing: badgesEnabled.value,
      marketBorderColor:
        brandContext?.adTemplateBranding.value.marketBorderColor,
      isCarousel: carouselContext.value.isCarousel,
      stackedBadges: stackedBadges.value
    }));
    const classes = useStyles(reactiveStyles);

    const eventTextSizes = computed(() => ({
      min: layoutClass?.size?.value.name === SKYSCRAPER.name ? 12 : 14,
      max: layoutClass?.size?.value.name === SKYSCRAPER.name ? 14 : 17
    }));

    return {
      classes,
      eventTextSizes,
      layoutClass: computed(() => layoutClass?.size.value.name),
      isCarousel: computed(() => carouselContext.value.isCarousel),
      badgesEnabled,
      vsHorizontalLine: computed(
        () => layoutClass?.size.value.name === SKYSCRAPER.name
      ),
      stackedBadges,
      competitors,
      useCompetitors,
      eventLink: computed(() => event.value?.eventUrl as string),
      eventName: computed(() => event.value?.name as string),
      handleClick,
      fixtureNameStyle: {
        get: (brand: Brand | undefined) =>
          brand?.adTemplate.market.teamNameFont || "",
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>
